/**
 * "My members": everyone assigned to the signed-in stylist, sorted so the
 * members who need something come first.
 *
 * The dashboard is the only required read. Tomorrow's outfit comes from the
 * review queue beside it, and degrades to "not shown" on its own rather than
 * blanking the list.
 */

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw01 } from '@untitledui/icons';
import { Link } from 'react-router';
import type { DashboardMember, DashboardResponse } from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, MetaItem, StatRow, StatTile } from '@ui/console/primitives';
import { TextField } from '@ui/console/forms';
import { Page, ResourceState } from './components';
import { accountStatus } from './member-format';
import { GarmentThumbs, MemberAvatar, PlainBadge } from './member-ui';
import { memberService, type MemberService } from './member-service';
import { reviewService, type QueuedOutfit, type ReviewService } from './review-service';

const QUEUE_READ_LIMIT = 200;

export type MemberSignals = {
  tomorrow?: QueuedOutfit;
};

export type AttentionItem = { label: string; color: 'warning' | 'blue' | 'error' | 'gray' };

export function attentionFor(member: DashboardMember, signals: MemberSignals): AttentionItem[] {
  const items: AttentionItem[] = [];
  if (signals.tomorrow) items.push({ label: "Tomorrow's outfit to review", color: 'blue' });
  if (member.onboarding_scheduled) items.push({ label: 'Onboarding in progress', color: 'gray' });
  return items;
}

function urgency(signals: MemberSignals): number {
  return signals.tomorrow ? 1 : 0;
}

export function sortByAttention<T extends { member: DashboardMember; signals: MemberSignals }>(
  rows: T[],
): T[] {
  return [...rows].sort(
    (a, b) => urgency(b.signals) - urgency(a.signals) || a.member.name.localeCompare(b.member.name),
  );
}

type Loaded = {
  dashboard: DashboardResponse;
  signals: Map<string, MemberSignals>;
  queueRead: boolean;
  queueTotal: number;
};

async function loadSignals(
  dashboard: DashboardResponse,
  review: ReviewService,
): Promise<Pick<Loaded, 'signals' | 'queueRead' | 'queueTotal'>> {
  const signals = new Map<string, MemberSignals>(dashboard.members.map((m) => [m.id, {}]));
  const queue = await review
    .loadQueue({ reviewStatus: 'awaiting_review', scope: 'mine', limit: QUEUE_READ_LIMIT })
    .catch(() => undefined);
  for (const outfit of queue?.outfits ?? []) {
    const entry = signals.get(outfit.memberId);
    if (entry && !entry.tomorrow) entry.tomorrow = outfit;
  }
  return { signals, queueRead: Boolean(queue), queueTotal: queue?.total ?? 0 };
}

function MemberCard({ member, signals }: { member: DashboardMember; signals: MemberSignals }) {
  const attention = attentionFor(member, signals);
  return (
    <li className="min-w-0">
      <Link
        to={`/clients/${encodeURIComponent(member.id)}`}
        aria-label={`Open ${member.name}`}
        className="flex h-full flex-col gap-3 rounded-xl border border-secondary bg-primary p-4 outline-brand hover:border-brand focus-visible:outline-2"
      >
        <div className="flex items-center gap-3">
          <MemberAvatar name={member.name} src={signals.tomorrow?.context.avatarUrl} />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold text-primary">{member.name}</span>
            {member.email ? (
              <span className="truncate text-xs text-secondary">{member.email}</span>
            ) : null}
            <span className="truncate text-xs text-tertiary">{accountStatus(member.status)}</span>
          </div>
        </div>
        {attention.length ? (
          <div className="flex flex-wrap gap-1.5" aria-label="Needs attention">
            {attention.map((item) => (
              <PlainBadge key={item.label} label={item.label} color={item.color} />
            ))}
          </div>
        ) : (
          <span className="text-xs text-tertiary">Nothing waiting on you</span>
        )}
        {signals.tomorrow ? (
          <GarmentThumbs
            garments={signals.tomorrow.garments}
            label={`${member.name}'s outfit for tomorrow`}
            size="xs"
          />
        ) : null}
      </Link>
    </li>
  );
}

export function MyMembers({
  members = memberService,
  review = reviewService,
}: {
  members?: MemberService;
  review?: ReviewService;
}) {
  const [resource, setResource] = useState<{ loading: boolean; data?: Loaded; error?: string }>({
    loading: true,
  });
  const [filter, setFilter] = useState('');
  const load = useCallback(async () => {
    setResource({ loading: true });
    try {
      const dashboard = await members.dashboard();
      const rest = await loadSignals(dashboard, review);
      setResource({ loading: false, data: { dashboard, ...rest } });
    } catch (cause) {
      setResource({
        loading: false,
        error: cause instanceof Error ? cause.message : 'Your members could not be loaded.',
      });
    }
  }, [members, review]);
  useEffect(() => void load(), [load]);

  const data = resource.data;
  const rows = data
    ? sortByAttention(
        data.dashboard.members.map((member) => ({
          member,
          signals: data.signals.get(member.id) ?? {},
        })),
      )
    : [];
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? rows.filter((row) =>
        [row.member.name, row.member.email ?? ''].some((text) =>
          text.toLowerCase().includes(needle),
        ),
      )
    : rows;
  const needing = rows.filter((row) =>
    attentionFor(row.member, row.signals).some((a) => a.color !== 'gray'),
  );

  return (
    <Page
      title="My members"
      description="Everyone assigned to you, with whoever needs something from you first."
      actions={
        <Button
          size="sm"
          color="secondary"
          iconLeading={RefreshCw01}
          isLoading={resource.loading}
          onClick={() => void load()}
        >
          Refresh
        </Button>
      }
      meta={
        data ? (
          <MetaItem label="Assigned to you" value={String(data.dashboard.total || rows.length)} />
        ) : undefined
      }
    >
      <ResourceState
        loading={resource.loading}
        loadingLabel="your members"
        error={resource.error}
        empty={rows.length === 0}
        emptyTitle="No members assigned yet"
        emptyMessage="When an administrator assigns members to you, they appear here."
        onRetry={() => void load()}
      >
        {data ? (
          <div className="flex flex-col gap-6">
            <StatRow>
              <StatTile
                label="Need attention"
                value={String(needing.length)}
                hint="Members with an outfit to review for tomorrow"
                {...(needing.length ? { tone: 'warning' as const, statusLabel: 'act today' } : {})}
              />
              <StatTile
                label="Outfits to review"
                value={data.queueRead ? String(data.queueTotal) : '—'}
                hint={
                  data.queueRead
                    ? "Your members' outfits for tomorrow awaiting review"
                    : "Tomorrow's queue could not be read"
                }
              />
              <StatTile
                label="Assigned members"
                value={String(data.dashboard.total || rows.length)}
                hint={
                  (data.dashboard.total ?? 0) > rows.length
                    ? `Showing the first ${rows.length}`
                    : 'All shown below'
                }
              />
            </StatRow>
            {data.queueRead && data.queueTotal > QUEUE_READ_LIMIT ? (
              <Callout tone="neutral" title="Some outfits may not be flagged">
                Your members have more than {QUEUE_READ_LIMIT} outfits awaiting review, so only the
                first {QUEUE_READ_LIMIT} were checked. Tomorrow's outfits has the full list.
              </Callout>
            ) : null}
            <div className="max-w-sm">
              <TextField
                label="Find a member by name or email"
                type="search"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
            {shown.length ? (
              <ul
                aria-label="Your members"
                className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
              >
                {shown.map((row) => (
                  <MemberCard key={row.member.id} member={row.member} signals={row.signals} />
                ))}
              </ul>
            ) : (
              <Callout tone="neutral" title="No members match that name or email" />
            )}
          </div>
        ) : null}
      </ResourceState>
    </Page>
  );
}
